import { EditorView } from '@codemirror/view'
import { HighlightStyle, syntaxHighlighting } from '@codemirror/language'
import { tags as t } from '@lezer/highlight'

/**
 * The code editor's look: highlight.js's GitHub-dark palette
 * (`highlight.js/styles/github-dark.css`, which the database viewer's SQL editor
 * still uses), so both read the same. Popups (completion, hover, search) take the
 * app theme's variables so they sit with the rest of the UI.
 */

const FG = '#c9d1d9'
const BG = '#0d1117'

export const editorTheme = EditorView.theme({
  '&': { color: FG, backgroundColor: BG, height: '100%', fontSize: '14px' },
  '&.cm-focused': { outline: 'none' },
  '.cm-scroller': {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
    lineHeight: '20px',
  },
  '.cm-content': { padding: '16px 0', caretColor: '#e4e4e7' },
  '.cm-line': { padding: '0 16px 0 12px' },
  '.cm-cursor, .cm-dropCursor': { borderLeftColor: '#e4e4e7' },
  '&.cm-focused > .cm-scroller > .cm-selectionLayer .cm-selectionBackground, .cm-selectionBackground, .cm-content ::selection':
    { backgroundColor: 'rgba(56, 139, 253, 0.35)' },
  '.cm-activeLine': { backgroundColor: 'rgba(110, 118, 129, 0.08)' },
  '.cm-gutters': { backgroundColor: BG, color: '#6e7681', border: 'none', borderRight: '1px solid #21262d' },
  '.cm-gutters .cm-gutterElement': { padding: '0 8px 0 12px' },
  '.cm-activeLineGutter': { backgroundColor: 'transparent', color: FG },
  '.cm-foldPlaceholder': { backgroundColor: '#21262d', border: 'none', color: '#8b949e' },
  '.cm-matchingBracket, &.cm-focused .cm-matchingBracket': { backgroundColor: 'rgba(63, 185, 80, 0.25)', outline: 'none' },
  '.cm-searchMatch': { backgroundColor: 'rgba(251, 191, 36, 0.25)', outline: '1px solid rgba(251, 191, 36, 0.5)' },
  '.cm-searchMatch.cm-searchMatch-selected': { backgroundColor: 'rgba(251, 191, 36, 0.6)' },
  '.cm-selectionMatch': { backgroundColor: 'rgba(110, 118, 129, 0.25)' },

  // ── Popups: the app's theme ───────────────────────────────────────────
  '.cm-tooltip': {
    backgroundColor: 'var(--bg-secondary, #18181b)',
    color: 'var(--text-primary, #fafafa)',
    border: '1px solid var(--border-secondary, #3f3f46)',
    borderRadius: '6px',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.35)',
    fontSize: '12px',
  },
  '.cm-tooltip.cm-tooltip-autocomplete > ul': { fontFamily: 'inherit', maxHeight: '220px' },
  '.cm-tooltip.cm-tooltip-autocomplete > ul > li': { padding: '2px 8px', lineHeight: '20px' },
  '.cm-tooltip-autocomplete ul li[aria-selected]': {
    backgroundColor: 'var(--btn-primary-bg, #2563eb)',
    color: '#fff',
  },
  '.cm-completionLabel': { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' },
  '.cm-completionDetail': { opacity: '0.65', fontStyle: 'normal', marginLeft: '12px' },
  '.cm-tooltip.cm-completionInfo': { padding: '0', width: '380px', maxWidth: '380px', maxHeight: '260px', overflowY: 'auto' },
  '.cm-completionInfo .cm-papan-doc': { padding: '6px 10px' },
  '.cm-papan-signature': { padding: '4px 8px', maxWidth: 'min(560px, calc(100vw - 32px))', pointerEvents: 'none' },
  '.cm-papan-signature .cm-papan-doc-title': { fontSize: '11px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginBottom: '2px', paddingBottom: '2px', borderBottom: 'none' },
  '.cm-papan-signature p': {
    fontSize: '11px', display: '-webkit-box', WebkitLineClamp: '2', WebkitBoxOrient: 'vertical', overflow: 'hidden', margin: '0',
  },
  '.cm-completionMatchedText': { textDecoration: 'none', fontWeight: '600' },
  '.cm-tooltip-hover': { maxWidth: '460px', maxHeight: '260px', overflowY: 'auto' },
  '.cm-papan-doc': { padding: '6px 10px', lineHeight: '1.45', color: 'var(--text-secondary, #a1a1aa)' },
  '.cm-papan-doc p': { margin: '0 0 6px' },
  '.cm-papan-doc p:last-child': { marginBottom: '0' },
  '.cm-papan-doc code': {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '11px',
    color: 'var(--text-primary, #fafafa)', backgroundColor: 'var(--bg-tertiary, #27272a)', padding: '0 3px', borderRadius: '3px',
  },
  '.cm-papan-doc-title': {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '12px', color: 'var(--text-primary, #fafafa)',
    whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: '0 0 6px', paddingBottom: '6px',
    borderBottom: '1px solid var(--border-secondary, #3f3f46)',
  },
  '.cm-papan-doc-title:last-child': { margin: '0', paddingBottom: '0', borderBottom: 'none' },
  '.cm-papan-doc-code': {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '11px', color: 'var(--text-primary, #fafafa)',
    whiteSpace: 'pre', overflowX: 'auto', margin: '0 0 6px',
  },

  '.cm-panels': { backgroundColor: 'var(--bg-secondary, #18181b)', color: 'var(--text-primary, #fafafa)' },
  '.cm-panels.cm-panels-top': { borderBottom: '1px solid var(--border-secondary, #3f3f46)' },
  '.cm-panel.cm-search': { padding: '6px 10px', fontSize: '12px' },
  '.cm-panel.cm-search input, .cm-panel.cm-search button': { fontSize: '12px' },
  '.cm-textfield': {
    backgroundColor: 'var(--input-bg, #27272a)',
    border: '1px solid var(--input-border, #3f3f46)',
    color: 'inherit',
    borderRadius: '4px',
  },
  '.cm-button': {
    backgroundImage: 'none',
    backgroundColor: 'var(--btn-secondary-bg, #27272a)',
    border: '1px solid var(--border-secondary, #3f3f46)',
    color: 'inherit',
    borderRadius: '4px',
  },
}, { dark: true })

/** github-dark's token colours. */
export const editorHighlight = syntaxHighlighting(HighlightStyle.define([
  { tag: [t.keyword, t.modifier, t.operatorKeyword, t.controlKeyword, t.definitionKeyword, t.moduleKeyword], color: '#ff7b72' },
  { tag: [t.string, t.special(t.string), t.regexp], color: '#a5d6ff' },
  { tag: [t.comment, t.lineComment, t.blockComment, t.docComment], color: '#8b949e', fontStyle: 'italic' },
  { tag: [t.function(t.variableName), t.function(t.propertyName), t.className, t.definition(t.function(t.variableName))], color: '#d2a8ff' },
  { tag: [t.number, t.bool, t.null, t.atom, t.constant(t.variableName), t.propertyName, t.attributeName, t.special(t.variableName)], color: '#79c0ff' },
  { tag: [t.typeName, t.standard(t.variableName), t.namespace, t.labelName], color: '#ffa657' },
  { tag: [t.tagName, t.heading], color: '#7ee787' },
  { tag: [t.heading], fontWeight: 'bold' },
  { tag: t.emphasis, fontStyle: 'italic' },
  { tag: t.strong, fontWeight: 'bold' },
  { tag: t.link, color: '#a5d6ff', textDecoration: 'underline' },
  { tag: [t.operator, t.punctuation, t.bracket, t.angleBracket], color: FG },
  { tag: t.invalid, color: '#f85149' },
]))
